<template>
  <v-dialog v-model="show" persistent min-width="500" max-width="50%">
    <v-card class="rounded-lg">
      <v-card-title color="primary" class="primary primary-title">
        <v-icon color="white darken-3">mdi-plus</v-icon>
        <span class="white--text title pl-2"> Add Patient Audiogram </span>
      </v-card-title>

      <v-card-text class="pt-4 ma-0">
        <v-container fluid fill-width class="pa-0 ma-2">
          <v-row align="center">
            <v-col cols="2" align="right" class="ma-0 pa-0 pr-3">
              Patient ID:
            </v-col>
            <v-col cols="9" align="left" class="ma-0 pa-0">
              <v-text-field v-model="patient_id" filled dense></v-text-field>
            </v-col>
            <v-col cols="1" align="left" class="ma-0 pa-0"></v-col>
          </v-row>

          <v-row align="center">
            <v-col cols="2" align="right" class="pa-0 ma-0 pr-3">
              <span>Plot color:</span>
            </v-col>
            <v-col cols="9" align="left" class="ma-0 pa-0">
              <v-btn-toggle v-model="color" class="pb-5">
                <v-btn
                  v-for="(co, co_index) in colorOptions"
                  :key="`vc-${co_index}`"
                >
                  <v-chip :color="co" class="px-4"></v-chip>
                </v-btn>
              </v-btn-toggle>
            </v-col>
            <v-col cols="6" align="left" class="ma-0 pa-0"></v-col>
          </v-row>

          <v-row align="center">
            <v-col cols="2" align="right" class="pa-0 ma-0 pr-3">
              <span>Conduction:</span>
            </v-col>
            <v-col cols="9" align="left" class="ma-0 pa-0">
              <v-btn-toggle v-model="conduction" class="pb-5">
                <v-btn
                  v-for="(co, co_index) in conductionOptions"
                  :key="`vc-${co_index}`"
                >
                  {{ co }}
                </v-btn>
              </v-btn-toggle>
            </v-col>
            <v-col cols="6" align="left" class="ma-0 pa-0"></v-col>
          </v-row>

          <v-row align="center">
            <v-col cols="2" align="right" class="pa-0 ma-0 pr-3">
              <span>Frequencies:</span>
            </v-col>
            <v-col cols="9" align="left" class="ma-0 pa-0">
              <v-btn-toggle v-model="frequencyRange" class="pb-5">
                <v-btn
                  v-for="(fr, fr_index) in frequencyRangeOptions"
                  :key="`vc-${fr_index}`"
                >
                  {{ fr }}
                </v-btn>
              </v-btn-toggle>
            </v-col>
            <v-col cols="6" align="left" class="ma-0 pa-0"></v-col>
          </v-row>

          <v-row
            v-for="(hz, hz_index) in frequencies"
            :key="`fr-${hz_index}`"
            align="center"
          >
            <v-col cols="2" align="right" class="pa-0 ma-0 pr-3">
              <span>{{ hz }} Hz:</span>
            </v-col>
            <v-col cols="4" align="left" class="ma-0 pa-0">
              <v-text-field
                v-model="leftFreqs[hz]"
                filled
                dense
                label="left dB"
              ></v-text-field>
            </v-col>
            <v-col cols="1" align="left" class="ma-0 pa-0"></v-col>
            <v-col cols="4" align="left" class="ma-0 pa-0">
              <v-text-field
                v-model="rightFreqs[hz]"
                filled
                dense
                label="right dB"
              ></v-text-field>
            </v-col>
            <v-col cols="1" align="left" class="ma-0 pa-0"></v-col>
          </v-row>
        </v-container>
      </v-card-text>

      <v-divider></v-divider>
      <v-card-actions>
        <v-btn color="primary" @click="useTestData()">Use Test Data</v-btn>
        <v-btn
          class="primary text--white ma-0 pa-0 px-2 mx-3"
          @click="resetAll()"
          >Reset</v-btn
        >
        <v-btn
          class="primary text--white ma-0 pa-0 px-2 mx-3"
          @click="removeAll()"
          >Remove all</v-btn
        >

        <v-spacer></v-spacer>
        <v-btn text color="red lighten-2" @click="close()">
          <v-icon left>close</v-icon> Cancel
        </v-btn>
        <v-btn color="primary" @click="addAudiogram">Add</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { getBrewerColor, getBrewerQualitativePalettes } from '@/utils/colors';

let NORMAL_FREQS = [250, 500, 1000, 2000, 4000, 8000];
let HIGH_FREQS = [9000, 10000, 12000, 14000, 16000, 18000, 20000];

let TEST_DATA = {
  // normal frequencies
  normal: {
    air: {
      left: {
        250: 20,
        500: 30,
        1000: 40,
        2000: 45,
        4000: 60,
        8000: 80,
      },
      right: {
        250: 25,
        500: 35,
        1000: 45,
        2000: 50,
        4000: 65,
        8000: 85,
      },
    },
    bone: {
      left: {
        250: 0,
        500: 0,
        1000: 0,
        2000: 0,
        4000: 0,
        8000: 0,
      },
      right: {
        250: 5,
        500: 5,
        1000: 5,
        2000: 5,
        4000: 5,
        8000: 5,
      },
    },
  },
  high: {
    air: {
      left: {
        9000: 80,
        10000: 80,
        12000: 85,
        14000: 90,
        16000: 95,
        18000: 105,
        20000: 120,
      },
      right: {
        9000: 85,
        10000: 90,
        12000: 90,
        14000: 95,
        16000: 100,
        18000: 110,
        20000: 120,
      },
    },
    bone: {
      left: {
        9000: 10,
        10000: 10,
        12000: 10,
        14000: 10,
        16000: 10,
        18000: 10,
        20000: 10,
      },
      right: {
        9000: 15,
        10000: 15,
        12000: 15,
        14000: 15,
        16000: 15,
        18000: 15,
        20000: 15,
      },
    },
  },
};

export default {
  props: {
    show: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      frequencies: NORMAL_FREQS,
      conductionOptions: ['air', 'bone'],
      frequencyRangeOptions: ['normal', 'high'],
      leftFreqs: {},
      rightFreqs: {},
      patient_id: '',
      color: 0,
      conduction: 0,
      frequencyRange: 0,
      getBrewerColor: getBrewerColor,
      getBrewerQualitativePalettes: getBrewerQualitativePalettes,
    };
  },
  computed: {
    colorOptions() {
      let colors = [];
      for (let i = 0; i < 10; ++i) {
        colors.push(getBrewerColor('Paired', 10, i));
      }
      return colors;
    },
  },
  watch: {
    frequencyRange(fr) {
      this.frequencies = fr == 0 ? NORMAL_FREQS : HIGH_FREQS;
    },
  },
  mounted() {
    this.resetAll();
  },
  methods: {
    close() {
      this.$emit('dialogClosed');
    },
    addAudiogram() {
      let al = {
        name: this.patient_id,
        ear: 'L',
        type: this.conductionOptions[this.conduction],
        color: this.colorOptions[this.color],
        freqs: {},
      };
      let ar = {
        name: this.patient_id,
        ear: 'R',
        type: this.conductionOptions[this.conduction],
        color: this.colorOptions[this.color],
        freqs: {},
      };
      this.frequencies.forEach(f => {
        if (f in this.leftFreqs && this.leftFreqs[f] != null) {
          al['freqs'][f] = [this.leftFreqs[f]];
        }
        if (f in this.rightFreqs && this.rightFreqs[f] != null) {
          ar['freqs'][f] = [this.rightFreqs[f]];
        }
      });
      this.$emit('addAudiogram', { L: al, R: ar });
      this.close();
    },
    useTestData() {
      let freqs = this.frequencyRangeOptions[this.frequencyRange];
      let conduction = this.conductionOptions[this.conduction];
      this.leftFreqs = TEST_DATA[freqs][conduction]['left'];
      this.rightFreqs = TEST_DATA[freqs][conduction]['right'];
      this.patient_id = 'Patient_12345';
    },
    resetAll() {
      this.frequencies.forEach(f => {
        this.patient_id = null;
        this.leftFreqs[f] = null;
        this.rightFreqs[f] = null;
      });
    },
    removeAll() {
      this.$emit('removeAllUserAudiograms');
    },
  },
};
</script>

<style lang="scss" scoped></style>
